import React, { Component } from "react";
import { getUserId, getAccessToken } from "../util/storage/accessTokenStorage";
import fetchDataForCreate from "../util/forms/createFetch";
import fetchDataForEdit from "../util/forms/editFetch";
import LoadAwareFormLayout from "../util/forms/components/LoadAwareFormLayout";
import onFormEdit from "../util/forms/onFormEdit";
import redirectCreate from "../util/forms/redirectCreate";
import redirectUpdate from "../util/forms/redirectUpdate";
import dispatchSchemaRequest from "../util/forms/dispatchSchemaRequest";
import onEditDelete from "../util/forms/onEditDelete";
import dispatchSchemaCreate from "../util/forms/dispatchSchemaCreate";
import dispatchSchemaUpdate from "../util/forms/dispatchSchemaUpdate";
import dispatchFileUpload from "../util/forms/dispatchFileUpload";
import PropTypes from "prop-types";
import { withRouterUtilities } from "../util/withRouterUtilities";
import { withNavigationContext } from "../util/withNavigationContext";
import { withFormik } from "formik";
import { withDataMapping } from "../util/hocs/withDataMapping";
import dispatchCallWithOptions from "../util/forms/dispatchCallWithOptions";
import addToFormArray from "../util/forms/addToFormArray";
import onArrayFormEdit from "../util/forms/onArrayFormEdit";
import removeFromFormArray from "../util/forms/removeFromFormArray";

class EditableDataContainer extends Component {
  componentDidMount() {
    if (this.isCreate()) {
      fetchDataForCreate(this, false);
    } else {
      fetchDataForEdit(this, false);
    }
  }

  isCreate = () => {
    return !!this.props.isCreate || !this.props.loadEndpoint;
  };

  doSubmit = (
    data,
    message,
    submitEndpoint,
    redirectUrl,
    shouldSetNextHistory = true
  ) => {
    if (this.props.isStep && (this.props.redirectUrl || redirectUrl)) {
      const url = redirectUrl ? redirectUrl : this.props.redirectUrl;
      return this.props.navigationContext.goNext(url, shouldSetNextHistory);
    }
    this.isCreate()
      ? redirectCreate(this, data, message, submitEndpoint, redirectUrl)
      : redirectUpdate(this, data, message, submitEndpoint, redirectUrl);
  };

  render() {
    const childrenWithProps = React.Children.map(this.props.children, (child) =>
      React.cloneElement(child, {
        ...this.props,
        autoSuggest: this.props.autoSuggest,
        myUser: this.props.myUser,
        id: this.props.id,
        accessToken: getAccessToken(),
        onFormEdit: (event) => {
          const isSwitchAndBlurMode =
            event.target.isNonBlurrable === true &&
            this.props.mutateRedux === "blur";
          if (
            !this.props.mutateRedux ||
            this.props.mutateRedux === "always" ||
            isSwitchAndBlurMode
          ) {
            onFormEdit(event.target.name, event.target.value, this);
          }
          this.props.handleChange(event);
        },
        onArrayFormEdit: (event) => {
          //it is assumed you want to mutate redux if you're using array mutations through the editable data container
          const fullName = event.target.name;
          const value = event.target.value;
          const fullNameArray = fullName.split(".");
          const length = fullNameArray.length;
          const name = fullNameArray[length - 1];
          const index = fullNameArray[length - 2];

          onArrayFormEdit(name, value, index, this);
        },
        customFormEdit: (name, value) => {
          if (!this.props.mutateRedux || this.props.mutateRedux === "always") {
            onFormEdit(name, value, this);
          }
          const event = { target: { name, value } };
          this.props.handleChange(event);
        },
        addToFormArray: (item) => {
          addToFormArray(item, this);
        },
        removeFromFormArray: (index) => {
          removeFromFormArray(index, this);
        },
        onBlur: (event) => {
          this.props.handleBlur(event);
          if (this.props.mutateRedux === "blur") {
            onFormEdit(event.target.name, event.target.value, this);
          }
        },
        onlyMutateRedux: (name, value) => {
          onFormEdit(name, value, this);
        },
        onSubmit: (
          payload = null,
          message = null,
          submitEndpoint = null,
          redirectUrl = null,
          shouldSetNextHistory = true
        ) => {
          this.props
            .validateForm()
            .then((result) => {
              if (!Object.keys(result).length) {
                // no errors
                this.doSubmit(
                  payload,
                  message,
                  submitEndpoint,
                  redirectUrl,
                  shouldSetNextHistory
                );
              } else {
                console.error({ validationErrorResult: result });
              }
            })
            .catch((er) => console.error({ submissionRejectionError: er }));
        },
        fileUpload: (file, property) =>
          dispatchFileUpload(this, file, property),
        avatarUpdateLabel: this.props.avatarUpdateLabel || "Add Picture",
        isCreate: this.isCreate(),
        userId: getUserId(),
        isLoading: this.props.loading,
        fetchData: this.isCreate() ? fetchDataForCreate : fetchDataForEdit,
        schemaRequest: () => dispatchSchemaRequest(this),
        update: (submitEndpoint, payload, callback) =>
          dispatchSchemaUpdate(this, submitEndpoint, payload, callback),
        create: (submitEndpoint, payload, callback) =>
          dispatchSchemaCreate(this, submitEndpoint, payload, callback),
        dispatchCallWithOptions: (options) =>
          dispatchCallWithOptions(this, options),
        onDelete: (submitEndpoint = null, redirectUrl = null) =>
          onEditDelete(this, submitEndpoint, redirectUrl),
        redirectUpdate,
        redirectUrl: this.props.redirectUrl,
        values: this.props.values,
        errors: this.props.errors,
        dispatch: this.props.dispatch,
        loadEndpoint: this.props.loadEndpoint,
      })
    );

    const loadingState = this.props.loading ? "loading" : "hide";

    return (
      <form onSubmit={(e) => e.preventDefault()}>
        <LoadAwareFormLayout
          id={this.props.id}
          wrapperClassName={this.props.className}
          childrenWithProps={childrenWithProps}
          loadingState={loadingState}
          loading={this.props.loading}
        />
      </form>
    );
  }
}

//User facing props declaration
EditableDataContainer.propTypes = {
  id: PropTypes.string.isRequired,
  submitEndpoint: PropTypes.string,
  loadEndpoint: PropTypes.string,
  avatarUpdateLabel: PropTypes.string,
  accessToken: PropTypes.string,
  successMessage: PropTypes.string,
  redirectUrl: PropTypes.string,
  initialData: PropTypes.any,
  autoSuggestEndpoints: PropTypes.object,
  validationSchema: PropTypes.object,
  isCreate: PropTypes.bool,
  mutateRedux: PropTypes.oneOf(["always", "blur", "never"]),
};

export default withNavigationContext(
  withRouterUtilities(
    withDataMapping(
      withFormik({
        enableReinitialize: true, // Populates form values after loading finishes

        mapPropsToValues: ({ data, mapPropsToValues, ...rest }) => {
          if (mapPropsToValues) {
            return mapPropsToValues({ data, ...rest });
          }
          return {
            ...data,
          };
        },

        validationSchema: (props) => props.validationSchema,
        validateOnChange: false,
        validateOnMount: true,

        handleSubmit: (values, { props }) => props.onSubmit(values),
      })(EditableDataContainer)
    )
  )
);
